/**
 * video-play 媒体同步引擎 —— 方案 V1.3（仅北京，朝阳零接触）
 *
 * 职责：Cache Storage 的【唯一写入口】。仅在用户点击页面右上角"同步"按钮后运行
 * （无任何自动/定时触发），把北京清单引用的全部媒体（封面+图片+视频，按 URL 去重）
 * 下载进 vl-media 缓存；Service Worker 只读不写。
 *
 * 硬约束（来自交叉验证评审，违反即返工）：
 *  - 只缓存 status===200；非 200 顺带 cache.delete() 清历史脏条目（如清单里 new8 两条
 *    指向不存在文件的 404 条目，入库会被 SW 永久回放且补传后无法自愈）
 *  - 变更检测用 ETag/If-None-Match 条件请求（304=精确未变，200=已变），不用体积启发式
 *  - fetch 一律 cache:'no-store'，避免同一份媒体再落 HTTP 磁盘缓存导致磁盘双份占用
 *  - navigator.storage.persist() 读返回值如实上报（演示机大概率不授予，不默认成功）
 *  - localStorage 心跳锁防多标签页重复同步
 *  - 控制台日志前缀 [媒体缓存]，逐文件一行 + 结束汇总，与面板进度数据同源
 */

const CACHE_NAME = 'vl-media';
const LOCK_KEY = 'vl_media_sync_lock';
const LOCK_TTL = 15 * 1000;      // 心跳 5s 一跳，超过 15s 未心跳视为持锁方已退出
const CHECK_CONCURRENCY = 5;     // 条件检查阶段并发数（评审 P1-2：不要 56 次串行）

const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp'];

const LOG = '[媒体缓存]';
const MB = 1024 * 1024;

function fmtMB(bytes) {
  if (!bytes || bytes <= 0) return '0MB';
  return (bytes / MB).toFixed(1) + 'MB';
}

function fmtTime(ms) {
  const s = Math.round(ms / 1000);
  if (s < 60) return s + '秒';
  const m = Math.floor(s / 60);
  return m + '分' + (s % 60) + '秒';
}

function getExt(url) {
  const m = /\.([a-z0-9]+)$/i.exec((url || '').split('?')[0]);
  return m ? m[1].toLowerCase() : '';
}

function fileName(url) {
  const m = /([^/]+)$/.exec(url || '');
  return m ? m[1] : url;
}

const TAB_ID = Math.random().toString(36).slice(2);

const state = {
  supported: typeof window !== 'undefined' && 'caches' in window,
  status: 'idle',      // idle | checking | syncing | paused | done
  urls: [],            // [{ url, kind: 'image' | 'video' }]
  sizes: {},           // url -> Content-Length（未知为 0）
  total: 0,
  done: 0,             // 已处理（跳过+成功+失败）
  okCount: 0,          // 本次新下载成功
  skipCount: 0,        // 304 跳过（缓存已是最新）
  failCount: 0,
  failures: [],        // [{ url, reason }]
  bytesDone: 0,        // 已处理文件累计字节
  totalBytes: 0,
  current: '',
  persisted: null,     // persist() 返回值（true/false/null=未申请）
  usage: 0,
  quota: 0,
  quotaRisk: false,
  offline: false,      // 清单走了离线回退（由页面写入）
  startedAt: 0,
};

let onProgress = null;
let lockHeld = false;
let heartbeatTimer = null;
let pauseFlag = false;
let autoPausedByDialog = false;
let abortFlag = false;

function snapshot() {
  const pendingBytes = Math.max(state.totalBytes - state.bytesDone, 0);
  return {
    status: state.status,
    total: state.total,
    done: state.done,
    okCount: state.okCount,
    skipCount: state.skipCount,
    failCount: state.failCount,
    percent: state.total ? Math.round((state.done / state.total) * 100) : 0,
    current: state.current,
    bytesText: `${fmtMB(state.bytesDone)} / ${fmtMB(state.totalBytes)}`,
    storageText: state.quota ? `已用 ${fmtMB(state.usage)} / 可用 ${fmtMB(state.quota)}` : '',
    persistedText: state.persisted === null ? '未申请' : (state.persisted ? '已授予' : '未授予'),
    quotaRisk: state.quotaRisk && pendingBytes > 0,
    failures: state.failures.slice(),
  };
}

function emit() {
  if (onProgress) {
    try { onProgress(snapshot()); } catch (e) { /* 页面回调异常不影响同步 */ }
  }
}

function sleep(ms) {
  return new Promise(r => setTimeout(r, ms));
}

// 暂停/中止闸门：下载循环每个文件之间调用
async function gate() {
  while (pauseFlag && !abortFlag) {
    await sleep(300);
  }
}

/* ---------------- 多标签页锁（localStorage 心跳） ---------------- */

function readLock() {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function acquireLock() {
  const cur = readLock();
  if (cur && cur.tab !== TAB_ID && Date.now() - cur.ts < LOCK_TTL) {
    return false; // 其它标签页正在同步
  }
  try {
    localStorage.setItem(LOCK_KEY, JSON.stringify({ tab: TAB_ID, ts: Date.now() }));
  } catch (e) { /* localStorage 不可用时退化为无锁 */ }
  lockHeld = true;
  heartbeatTimer = setInterval(() => {
    if (!lockHeld) return;
    try { localStorage.setItem(LOCK_KEY, JSON.stringify({ tab: TAB_ID, ts: Date.now() })); } catch (e) { /* ignore */ }
  }, 5000);
  return true;
}

function releaseLock() {
  lockHeld = false;
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  const cur = readLock();
  if (cur && cur.tab === TAB_ID) {
    try { localStorage.removeItem(LOCK_KEY); } catch (e) { /* ignore */ }
  }
}

/* ---------------- 清单收集（仅北京 /videos/，按 URL 去重） ---------------- */

function collectUrls(configData) {
  const seen = new Set();
  const urls = [];
  (configData || []).forEach(cat => {
    (cat && cat.files || []).forEach(f => {
      [f.path, f.thumbnail].forEach(u => {
        if (!u || typeof u !== 'string') return;
        // 仅接受北京前缀；/videos_cy/ 与其它来源一律不碰（朝阳零接触）
        if (!u.startsWith('/videos/')) return;
        if (seen.has(u)) return;
        seen.add(u);
        urls.push({ url: u, kind: IMAGE_EXTS.includes(getExt(u)) ? 'image' : 'video' });
      });
    });
  });
  return urls;
}

/* ---------------- 状态刷新 ---------------- */

async function refreshStorage() {
  if (navigator.storage && navigator.storage.estimate) {
    try {
      const est = await navigator.storage.estimate();
      state.usage = est.usage || 0;
      state.quota = est.quota || 0;
    } catch (e) { /* ignore */ }
  }
}

async function requestPersist() {
  if (navigator.storage && navigator.storage.persist) {
    try {
      state.persisted = await navigator.storage.persist();
    } catch (e) {
      state.persisted = false;
    }
  }
  if (state.persisted) {
    console.log(`${LOG} 持久化存储：已授予（磁盘紧张时浏览器不会优先清除本站缓存）`);
  } else {
    console.warn(`${LOG} 持久化存储：未授予（缓存属 best-effort，磁盘紧张时可能被系统清理，可重新同步）`);
  }
}

/* ---------------- 同步主流程 ---------------- */

async function startSync(configData, cb) {
  if (!state.supported) {
    console.warn(`${LOG} 当前浏览器不支持 Cache Storage，无法同步`);
    return;
  }
  // SW 未接管本页时同步无意义（下载的内容只有 SW 会读取）：开发模式/SW 注册失败均拒绝
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    console.warn(`${LOG} Service Worker 未生效（开发模式或注册失败），本地缓存不可用，已取消同步`);
    return;
  }
  if (state.status === 'syncing' || state.status === 'checking') return; // 已在进行
  if (state.status === 'paused') {
    pauseFlag = false;
    state.status = 'syncing';
    emit();
    return;
  }

  onProgress = cb || onProgress;

  if (!acquireLock()) {
    console.warn(`${LOG} 另一个标签页正在同步，本页跳过（锁 15 秒无心跳自动失效）`);
    return;
  }

  abortFlag = false;
  pauseFlag = false;

  state.urls = collectUrls(configData);
  state.total = state.urls.length;
  state.sizes = {};
  state.done = 0;
  state.okCount = 0;
  state.skipCount = 0;
  state.failCount = 0;
  state.failures = [];
  state.bytesDone = 0;
  state.totalBytes = 0;
  state.current = '';
  state.startedAt = Date.now();

  await requestPersist();
  await refreshStorage();

  if (!state.total) {
    state.status = 'done';
    console.warn(`${LOG} 清单为空，无可同步内容`);
    emit();
    releaseLock();
    return;
  }

  console.log(`${LOG} 开始同步：北京 共 ${state.total} 个文件（已有缓存与待下载情况检查中…）`);

  const cache = await caches.open(CACHE_NAME);

  /* 阶段一：逐条检查（4~6 并发）——已有且未变化的记跳过；其余进入待下载 */
  state.status = 'checking';
  emit();

  const pending = [];
  const checkQueue = state.urls.slice();

  async function checkOne(item) {
    const cached = await cache.match(item.url, { ignoreVary: true });
    const size = cached ? (Number(cached.headers.get('Content-Length')) || 0) : 0;
    if (size) state.sizes[item.url] = size;

    if (!cached) {
      pending.push(item);
      return;
    }
    // ETag 条件请求：304=精确未变；200=内容已变需重下
    // X-VL-Sync 标记头让 SW 对本请求透传网络（见 sw.js，防止被本地旧副本拦截）
    const etag = cached.headers.get('ETag');
    let res = null;
    try {
      res = await fetch(item.url, {
        cache: 'no-store',
        headers: Object.assign(
          etag ? { 'If-None-Match': etag } : {},
          { 'X-VL-Sync': '1' }
        ),
      });
    } catch (e) {
      // 网络异常（如已断网）：保留缓存按跳过处理
      state.skipCount++;
      state.done++;
      state.bytesDone += size;
      return;
    }
    if (res.status === 304) {
      state.skipCount++;
      state.done++;
      state.bytesDone += size;
      console.log(`${LOG} ${state.done}/${state.total} 跳过(未变化 ETag 304) ${item.url}`);
    } else if (res.status === 200) {
      // 已变更：取消本次响应体（真正的下载交给下载阶段串行进行），不取消会占住连接堵塞后续请求
      if (res.body) { try { await res.body.cancel(); } catch (e) { /* ignore */ } }
      pending.push(item);
    } else {
      // 服务器非 304/200（如 404）：取消响应体，清掉脏缓存条目并计失败
      if (res.body) { try { await res.body.cancel(); } catch (e) { /* ignore */ } }
      await cache.delete(item.url).catch(() => {});
      state.failCount++;
      state.done++;
      state.failures.push({ url: item.url, reason: `HTTP ${res.status}` });
      console.warn(`${LOG} ${state.done}/${state.total} 失败 ${res.status}(已移除旧缓存条目) ${item.url}`);
    }
    state.current = fileName(item.url);
    emit();
  }

  const workers = [];
  for (let i = 0; i < CHECK_CONCURRENCY; i++) {
    workers.push((async () => {
      while (checkQueue.length) {
        if (abortFlag) return;
        const item = checkQueue.shift();
        if (!item) break;
        await checkOne(item);
      }
    })());
  }
  await Promise.all(workers);
  if (abortFlag) return;

  // 总体量按已知 size 汇总（404 与未知算 0）
  state.totalBytes = state.urls.reduce((s, it) => s + (state.sizes[it.url] || 0), 0);

  // 下载顺序：先图片/封面，视频按体积升序（让尽量多条目尽早可秒开）
  pending.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'image' ? -1 : 1;
    if (a.kind === 'video') {
      return (state.sizes[a.url] || Infinity) - (state.sizes[b.url] || Infinity);
    }
    return 0;
  });

  console.log(`${LOG} 检查完成：已是最新 ${state.skipCount} 个，待下载 ${pending.length} 个，总体量约 ${fmtMB(state.totalBytes)}`);

  // 配额预检：剩余空间明显放不下待下载量时提示（不阻断，下载中真正超限会按失败停止并提示）
  if (state.quota && pending.length) {
    const pendingBytes = pending.reduce((s, it) => s + (state.sizes[it.url] || 0), 0);
    if (state.quota - state.usage < pendingBytes * 0.9) {
      state.quotaRisk = true;
      console.warn(`${LOG} 可用空间 ${fmtMB(state.quota - state.usage)} 可能不足以放下待下载的 ${fmtMB(pendingBytes)}，` +
        `若 Chrome 开启了「关闭所有窗口时清除 Cookie 和网站数据」请关闭该设置后重试`);
    }
  }

  /* 阶段二：串行下载（视频一次一个，不占满带宽；弹窗打开时自动暂停） */
  state.status = 'syncing';
  emit();

  for (const item of pending) {
    if (abortFlag) return;
    await gate();
    if (abortFlag) return;

    state.current = fileName(item.url);
    emit();

    let res;
    try {
      // X-VL-Sync 标记头让 SW 透传网络，保证拿到的是服务器最新内容而非本地旧副本
      res = await fetch(item.url, { cache: 'no-store', headers: { 'X-VL-Sync': '1' } }); // 全量请求，不带 Range
    } catch (e) {
      state.failCount++;
      state.done++;
      state.failures.push({ url: item.url, reason: '网络错误' });
      console.warn(`${LOG} ${state.done}/${state.total} 失败 网络错误 ${item.url}`);
      emit();
      continue;
    }

    if (res.status !== 200) {
      // 只缓存 200；非 200 清掉可能存在的历史脏条目，避免 404 被 SW 永久回放
      await cache.delete(item.url).catch(() => {});
      state.failCount++;
      state.done++;
      state.failures.push({ url: item.url, reason: `HTTP ${res.status}` });
      console.warn(`${LOG} ${state.done}/${state.total} 失败 ${res.status}(不入库) ${item.url}`);
      emit();
      continue;
    }

    const size = Number(res.headers.get('Content-Length')) || state.sizes[item.url] || 0;
    try {
      await cache.put(item.url, res);
    } catch (e) {
      // QuotaExceededError 等：停止后续下载并明确提示
      state.failCount++;
      state.failures.push({ url: item.url, reason: '写入缓存失败(' + (e && e.name || 'Error') + ')' });
      console.warn(`${LOG} 写入失败，停止同步：${item.url} ${e && e.name}: ${e && e.message}`);
      await finish(cache, true);
      return;
    }
    // 未下载前不知道体积的文件在此补计入总体量（检查阶段只有已缓存条目才有 size）
    if (size) {
      state.totalBytes += size - (state.sizes[item.url] || 0);
      state.sizes[item.url] = size;
    }
    state.okCount++;
    state.done++;
    state.bytesDone += size;
    console.log(`${LOG} ${state.done}/${state.total} 完成` +
      (size ? ` +${fmtMB(size)} 累计 ${fmtMB(state.bytesDone)}/${fmtMB(state.totalBytes)} (${Math.round(state.done / state.total * 100)}%)` : '') +
      ` ${item.url}`);
    emit();
  }

  await finish(cache, false);
}

async function finish(cache, aborted) {
  await refreshStorage();
  state.status = 'done';
  state.current = '';
  emit();
  releaseLock();

  const cost = fmtTime(Date.now() - state.startedAt);
  if (aborted) {
    console.warn(`${LOG} ⚠️ 同步未完成：成功 ${state.okCount}、跳过 ${state.skipCount}、失败 ${state.failCount}，耗时 ${cost}。失败项：` +
      (state.failures.map(f => `${f.url}(${f.reason})`).join('；') || '无'));
  } else {
    const missNote = state.failCount
      ? `、失败 ${state.failCount}（${state.failures.map(f => f.reason).join('/') || '未知'}）`
      : '';
    console.log(`${LOG} ✅ 同步完成：成功 ${state.okCount}/${state.total}、跳过(已是最新) ${state.skipCount}${missNote}，` +
      `缓存占用约 ${fmtMB(state.usage)}，耗时 ${cost}`);
    if (state.failCount) {
      console.warn(`${LOG} 失败清单：\n` + state.failures.map(f => `  ${f.url} -> ${f.reason}`).join('\n'));
    }
  }
}

/* ---------------- 对外接口 ---------------- */

export default {
  /** 页面加载清单后初始化：统计已缓存条目数与存储状态（不发起任何下载） */
  async init(configData, cb) {
    if (!state.supported) return;
    onProgress = cb || onProgress;
    state.urls = collectUrls(configData);
    state.total = state.urls.length;
    if (state.status === 'syncing' || state.status === 'checking' || state.status === 'paused') return;

    // 缓存尚不存在时不开新（caches.open 会创建空缓存；SW 未生效的环境保持零痕迹）
    if (!(await caches.has(CACHE_NAME))) {
      console.log(`${LOG} 本地缓存现状：0/${state.total} 个文件已缓存（尚未同步过）`);
      emit();
      return;
    }

    try {
      const cache = await caches.open(CACHE_NAME);
      let cachedCount = 0;
      let cachedBytes = 0;
      for (const it of state.urls) {
        const hit = await cache.match(it.url, { ignoreVary: true });
        if (hit) {
          cachedCount++;
          const size = Number(hit.headers.get('Content-Length')) || 0;
          if (size) {
            state.sizes[it.url] = size;
            cachedBytes += size;
          }
        }
      }
      state.done = cachedCount;
      state.bytesDone = cachedBytes;
      state.totalBytes = cachedBytes;
      state.status = cachedCount >= state.total && state.total > 0 ? 'done' : 'idle';
      await refreshStorage();
      console.log(`${LOG} 本地缓存现状：${cachedCount}/${state.total} 个文件已缓存（约 ${fmtMB(cachedBytes)}）`);
      emit();
    } catch (e) { /* ignore */ }
  },

  /** 手动开始/继续同步 */
  start(configData, cb) {
    startSync(configData, cb).catch(err => {
      console.error(`${LOG} 同步异常终止:`, err);
      state.status = 'done';
      emit();
      releaseLock();
    });
  },

  pause() {
    if (state.status === 'syncing' || state.status === 'checking') {
      pauseFlag = true;
      state.status = 'paused';
      console.log(`${LOG} 已暂停：已缓存 ${state.done}/${state.total}，累计 ${fmtMB(state.bytesDone)}`);
      emit();
    }
  },

  resume() {
    if (state.status === 'paused') {
      pauseFlag = false;
      state.status = 'syncing';
      emit();
    }
  },

  /** 媒体弹窗打开时自动暂停（避免抢带宽），关闭后自动恢复 */
  autoPause() {
    if (state.status === 'syncing' || state.status === 'checking') {
      autoPausedByDialog = true;
      this.pause();
    }
  },

  autoResume() {
    if (autoPausedByDialog) {
      autoPausedByDialog = false;
      this.resume();
    }
  },

  /** 清空本地媒体缓存（含清单缓存），页面回到纯网络模式 */
  async clearAll() {
    abortFlag = true;
    pauseFlag = false;
    releaseLock();
    const hadBytes = state.bytesDone;
    try {
      await caches.delete(CACHE_NAME);
    } catch (e) { /* ignore */ }
    state.status = 'idle';
    state.done = 0;
    state.okCount = 0;
    state.skipCount = 0;
    state.failCount = 0;
    state.failures = [];
    state.bytesDone = 0;
    state.totalBytes = 0;
    state.current = '';
    state.persisted = null;
    await refreshStorage();
    console.log(`${LOG} 已清空本地缓存（释放约 ${fmtMB(hadBytes)}），页面回到纯网络模式`);
    abortFlag = false;
    emit();
  },

  /** 页面销毁（路由离开）时释放同步锁 */
  release() {
    releaseLock();
  },

  setOffline(v) {
    state.offline = !!v;
  },
};
